import React, { useRef, useState } from "react";
import { DeleteOutlined, EditOutlined, ExportOutlined, FolderOutlined } from "@ant-design/icons";
import { Button, Dropdown, List, MenuProps, Tooltip } from "antd";
import { Bookmark, Item } from "./bookmark.types";
import { DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "./utils";


interface BookmarkListItemProps {
	item: Bookmark;
	handleSelect: (id: string, action?: string, item?: Bookmark) => void;
	onDrop: (draggedItem: Item, targetId: string, position: "above" | "below" | "inside") => void;
	isSelected: boolean;
}

const BookmarkListItem: React.FC<BookmarkListItemProps> = ({
	item,
	handleSelect,
	onDrop,
	isSelected,
}) => {
	const nodeRef = useRef<HTMLElement | null>(null);
	const [dropPosition, setDropPosition] = useState<"above" | "below" | "inside" | null>(null);

	const [{ isDragging }, drag] = useDrag({
		type: item.type === "folder" ? ItemTypes.FOLDER : ItemTypes.BOOKMARK,
		item: { id: item.id, type: item.type },
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [{ isOver }, drop] = useDrop({
		accept: [ItemTypes.FOLDER, ItemTypes.BOOKMARK],
		drop: (draggedItem: Item, monitor: DropTargetMonitor) => {
			if (!nodeRef.current || !dropPosition) return;
			onDrop(draggedItem, item.id, dropPosition);
		},
		hover: (draggedItem: Item, monitor: DropTargetMonitor) => {
			if (!nodeRef.current || draggedItem.id === item.id) {
				setDropPosition(null);
				return;
			}
			const offset = monitor.getClientOffset();
			if (!offset) {
				setDropPosition(null);
				return;
			}
			const rect = nodeRef.current.getBoundingClientRect();
			const y = offset.y - rect.top;
			const heightThird = rect.height / 2;
			if (y < heightThird / 2) {
				setDropPosition("above");
			} else if (y > rect.height - heightThird / 2) {
				setDropPosition("below");
			} else {
				setDropPosition(item.type === "folder" ? "inside" : null);
			}
		},
		canDrop: (draggedItem: Item) => {
			if (draggedItem.id === item.id) return false;
			if (draggedItem.type === "folder" && item.type === "folder") {
				const isDescendant = (node: Bookmark, id: string): boolean => {
					if (!Array.isArray(node.children)) return false;
					return node.children.some(child => child.id === id || isDescendant(child, id));
				};
				return !isDescendant(item, draggedItem.id);
			}
			return true;
		},
		collect: monitor => ({
			isOver: monitor.isOver(),
		}),
	});

	const bookmarkContextMenu: MenuProps = {
		items: [
			...(item.type === "bookmark" && item.url
				? [
						{
							key: "open",
							label: "Open",
							icon: <ExportOutlined />,
							onClick: () => window.open(item.url, "_self"),
						},
						{
							key: "open-new-tab",
							label: "Open in new tab",
							icon: <ExportOutlined />,
							onClick: () => window.open(item.url, "_blank"),
						},
						{
							key: "open-new-window",
							label: "Open in new window",
							icon: <ExportOutlined />,
							onClick: () => window.open(item.url, "_blank", "noopener,noreferrer"),
						},
				  ]
				: []),
			{
				key: "edit",
				label: item.type === "bookmark" ? "Edit" : "Rename",
				icon: <EditOutlined />,
				onClick: () => handleSelect(item.id, "edit", item),
			},
			{
				key: "delete",
				label: "Delete",
				icon: <DeleteOutlined />,
				onClick: () => handleSelect(item.id, "delete"),
			},
		],
	};

	const folderContextMenu: MenuProps = {
		items: [
			{
				key: "new-bookmark",
				label: "New bookmark",
				icon: <ExportOutlined />,
				onClick: () => handleSelect(item.id, "bookmark"),
			},
			{
				key: "new-folder",
				label: "New folder",
				icon: <FolderOutlined />,
				onClick: () => handleSelect(item.id, "folder"),
			},
			{
				key: "edit",
				label: "Rename",
				icon: <EditOutlined />,
				onClick: () => handleSelect(item.id, "edit", item),
			},
			{
				key: "delete",
				label: "Delete",
				icon: <DeleteOutlined />,
				onClick: () => handleSelect(item.id, "delete"),
			},
		],
	};

	return (
		<List.Item
			ref={node => {
				nodeRef.current = node;
				drag(drop(node));
			}}
			className={`ant-list-item ${isSelected ? "selected" : ""} ${
				isOver && dropPosition === "inside" ? "dnd-over" : ""
			}`}
			style={{
				border: "none",
				padding: "8px 12px",
				borderRadius: "2px",
				marginBottom: "2px",
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				fontFamily: "Roboto, Arial, sans-serif",
				fontSize: "14px",
				opacity: isDragging ? 0.5 : 1,
				position: "relative",
			}}
			onClick={() => handleSelect(item.id, "select")}
			onDoubleClick={() => {
				if (item.type === "bookmark" && item.url) {
					window.open(item.url, "_blank");
				} else {
					handleSelect(item.id);
				}
			}}
		>
			{isOver && dropPosition === "above" && <div className="drop-line" style={{ top: 0 }} />}
			{isOver && dropPosition === "below" && <div className="drop-line" style={{ bottom: 0 }} />}
			<div
				onContextMenu={e => {
					e.stopPropagation();
				}}
				style={{ flex: 1, display: "flex", alignItems: "center" }}
			>
				<Dropdown
					menu={item.type === "bookmark" ? bookmarkContextMenu : folderContextMenu}
					trigger={["contextMenu"]}
				>
					<div style={{ display: "flex", alignItems: "center", width: "100%" }}>
						{item.type === "folder" ? (
							<FolderOutlined
								style={{
									marginRight: "8px",
									fontSize: "16px",
									color: isSelected ? "#5f6368" : "#5f6368",
								}}
							/>
						) : (
							<img
								src={(() => {
									const url = item.url;
									let updatedUrl: URL | string = "";
									try {
										updatedUrl = url ? new URL(url).hostname : "";
									} catch (error) {
										console.log(error, url)
									}
									return `https://www.google.com/s2/favicons?domain=${updatedUrl}`
								})()}
								alt=""
								style={{ marginRight: "8px", width: "16px", height: "16px" }}
							/>
						)}
						<span style={{ color: isSelected ? "#202124" : "#202124" }}>{item.title}</span>
					</div>
				</Dropdown>
			</div>
			<div className="action-buttons">
				<Tooltip title={item.type === "bookmark" ? "Edit" : "Rename"}>
					<Button
						type="text"
						icon={<EditOutlined />}
						size="small"
						onClick={e => {
							e.stopPropagation();
							handleSelect(item.id, "edit", item);
						}}
						style={{ color: isSelected ? "#1967d2" : "#5f6368", height: "22px" }}
					/>
				</Tooltip>
				<Tooltip title="Delete">
					<Button
						type="text"
						icon={<DeleteOutlined />}
						size="small"
						onClick={e => {
							e.stopPropagation();
							handleSelect(item.id, "delete");
						}}
						style={{ color: isSelected ? "#1967d2" : "#5f6368", height: "22px" }}
					/>
				</Tooltip>
			</div>
		</List.Item>
	);
};

export default BookmarkListItem;